import { dateLocale, t, useLanguage } from '@/lib/i18n'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import {
  getNotifications,
  markAllNotificationsSeen,
  markNotificationRead,
  markNotificationsSeen,
} from './notifications-gateway'
import {
  applyBatchSeen,
  applyMarkAllSeen,
  applyMarkRead,
  emptyNotificationsState,
  fromListResponse,
  insertRealtimeNotification,
  unseenIds,
  type NotificationsState,
} from './notifications-state'
import type { RealtimeNotificationInsert, StudentNotification } from './notifications-types'
import './notifications.css'

const PAGE_SIZE = 10
const LIST_KEY = ['notifications', 'list'] as const

type WriteNotice = 'failed' | null

function relativeTime(iso: string) {
  const diffMinutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (Number.isNaN(diffMinutes) || diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(iso).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
}

export function NotificationsBell() {
  useLanguage()
  const { demo } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [writeNotice, setWriteNotice] = useState<WriteNotice>(null)

  const query = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => fromListResponse(await getNotifications(PAGE_SIZE, 0)),
    enabled: !demo,
  })
  const state = demo ? emptyNotificationsState() : query.data

  // Realtime inserts from the existing bridge: prepend + bump counts locally
  // for an instant badge; the bridge separately invalidates ['notifications']
  // so the server list remains the authority.
  useEffect(() => {
    if (demo) return
    const onInsert = (event: Event) => {
      const detail = (event as CustomEvent<RealtimeNotificationInsert>).detail
      if (!detail?.id) return
      queryClient.setQueryData<NotificationsState>(LIST_KEY, (current) =>
        insertRealtimeNotification(current ?? emptyNotificationsState(), detail))
    }
    window.addEventListener('ielts-v2-notification', onInsert)
    return () => window.removeEventListener('ielts-v2-notification', onInsert)
  }, [demo, queryClient])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const recordWriteFailure = useCallback((error: unknown) => {
    setWriteNotice('failed')
  }, [])

  // Opening the panel marks the visible unseen items seen (Vue parity). Cache
  // state changes only on server success — a blocked write keeps the badge and
  // flags truthful instead of faking a cleared state.
  const markVisibleSeen = useCallback(async (current: NotificationsState) => {
    const ids = unseenIds(current)
    if (demo) return
    try {
      if (ids.length > 0) {
        const response = await markNotificationsSeen(ids)
        queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) =>
          value ? applyBatchSeen(value, response.notifications) : value)
      }
      // Counts are account-wide; the first page can leave older unseen items
      // on the badge. Opening the panel should clear that leftover too.
      const leftover = queryClient.getQueryData<NotificationsState>(LIST_KEY)
      if ((leftover?.unseenCount ?? current.unseenCount) > 0) {
        await markAllNotificationsSeen()
        queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) =>
          value ? applyMarkAllSeen(value) : value)
      }
      setWriteNotice(null)
    } catch (error) {
      recordWriteFailure(error)
    }
  }, [demo, queryClient, recordWriteFailure])

  const togglePanel = () => {
    const next = !open
    setOpen(next)
    if (next && state) void markVisibleSeen(state)
  }

  const onMarkAllSeen = async () => {
    try {
      await markAllNotificationsSeen()
      queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) => (value ? applyMarkAllSeen(value) : value))
      setWriteNotice(null)
    } catch (error) {
      recordWriteFailure(error)
    }
  }

  const onNotificationClick = async (notification: StudentNotification) => {
    if (!demo && (!notification.is_read || !notification.is_seen)) {
      try {
        const updated = await markNotificationRead(String(notification.id))
        queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) =>
          value ? applyMarkRead(value, updated) : value)
        setWriteNotice(null)
      } catch (error) {
        recordWriteFailure(error)
      }
    }
    if (notification.link) {
      setOpen(false)
      // The server link is a relative app path (e.g. /results/<id>); always
      // route internally, never treat it as an absolute URL.
      navigate(notification.link)
    }
  }

  const unseenCount = state?.unseenCount ?? 0

  return (
    <div className="notifications-bell" ref={containerRef}>
      <button
        type="button"
        className="notifications-bell__trigger"
        aria-label={unseenCount > 0 ? t("Notifications, {0} unseen", { "0": unseenCount }) : t("Notifications")}
        aria-expanded={open}
        onClick={togglePanel}
      >
        <Bell size={16} aria-hidden="true" />
        {unseenCount > 0 && (
          <span className="notifications-bell__badge" aria-hidden="true">{unseenCount > 9 ? '9+' : unseenCount}</span>
        )}
      </button>
      {open && (
        <section className="notifications-panel" aria-label={t("Notifications")}>
          <header>
            <h2>{t("Notifications")}</h2>
            {!demo && unseenCount > 0 && (
              <button type="button" onClick={() => void onMarkAllSeen()}>{t("Mark all as seen")}</button>
            )}
          </header>
          {writeNotice === 'failed' && (
            <p className="notifications-panel__notice" role="alert">{t("We could not update your notifications just now, so nothing has changed.")}</p>
          )}
          {demo && (
            <p className="notifications-panel__status" role="note">{t("The sample journal has no notifications.")}</p>
          )}
          {!demo && query.isPending && (
            <p className="notifications-panel__status" role="status">{t("Loading notifications…")}</p>
          )}
          {!demo && query.isError && (
            <div className="notifications-panel__status" role="alert">
              <p>{t("Your notifications could not be loaded.")}</p>
              <button type="button" className="text-[var(--accent-deep)] font-semibold" onClick={() => void query.refetch()}>{t("Retry")}</button>
            </div>
          )}
          {!demo && state && !query.isError && state.notifications.length === 0 && (
            <p className="notifications-panel__status">{t("No notifications yet")}</p>
          )}
          {!demo && state && state.notifications.length > 0 && (
            <ul className="notifications-panel__list">
              {state.notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className="notifications-panel__item"
                    data-read={notification.is_read}
                    onClick={() => void onNotificationClick(notification)}
                  >
                    <i aria-hidden="true" data-unread={!notification.is_read} />
                    <span>
                      <p>{notification.message}</p>
                      <time dateTime={notification.created_at}>{relativeTime(notification.created_at)}</time>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!demo && state?.hasMore && (
            <p className="notifications-panel__more">{t("Showing your latest ")}{PAGE_SIZE}{t(". Older ones are still kept on your account.")}</p>
          )}
        </section>
      )}
    </div>
  )
}
